import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Router} from '@angular/router';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {merge, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {FolderService} from '../../../services/folder/folder.service';
import {NotificationService} from '../../../services/common/notification.service';
import {FOLDER_ACCESS_LABELS, FolderScope, FolderSort, SimpleFolder, folderUserName} from '../../../dtos/folder/folder';
import {FolderActionsService} from '../../../services/folder/folder-actions.service';
import {folderErrorMessage, MAX_FOLDER_ITEMS} from '../../../shared/folder/folder-utils';

/**
 * overview of the user's Sammelmappen: own, shared and pending lists
 */
@Component({
  selector: 'app-folder-overview',
  templateUrl: './folder-overview.component.html',
  styleUrls: ['./folder-overview.component.scss'],
  standalone: false
})
export class FolderOverviewComponent implements AfterViewInit, OnDestroy {

  readonly maxItems = MAX_FOLDER_ITEMS;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly accessLabels = FOLDER_ACCESS_LABELS;
  readonly folderUserName = folderUserName;

  scope: FolderScope = 'ALL';
  searchCtrl = new FormControl('');
  folders: SimpleFolder[] = [];
  totalElements = 0;
  loading = true;

  pendingFolders: SimpleFolder[] = [];

  private reload$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(private folderService: FolderService,
              private folderActions: FolderActionsService,
              private notificationService: NotificationService,
              private router: Router) {
  }

  get displayedColumns(): string[] {
    const columns = ['name', 'itemCount'];
    if (this.scope !== 'OWNED') {
      columns.push('owner', 'access');
    }
    if (this.scope !== 'SHARED') {
      columns.push('shareCount');
    }
    columns.push('createdAt', 'updatedAt', 'actions');
    return columns;
  }

  ngAfterViewInit(): void {
    const search$ = this.searchCtrl.valueChanges.pipe(debounceTime(300), distinctUntilChanged());
    merge(this.sort.sortChange, search$, this.reload$).pipe(takeUntil(this.destroy$))
      .subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page, search$, this.reload$).pipe(
      startWith({}),
      switchMap(() => {
        this.loading = true;
        return this.folderService.getFolders({
          scope: this.scope,
          name: this.searchCtrl.value?.trim(),
          page: this.paginator.pageIndex,
          size: this.paginator.pageSize,
          sort: this.sort.active as FolderSort,
          order: this.sort.direction || 'asc'
        });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: page => {
        this.loading = false;
        this.folders = page.content;
        this.totalElements = page.totalElements;
      },
      error: error => {
        this.loading = false;
        this.notificationService.openSnackBar(folderErrorMessage(error));
      }
    });

    this.loadPending();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setScope(scope: FolderScope): void {
    this.scope = scope;
    this.reload$.next();
  }

  reload(): void {
    this.reload$.next();
  }

  loadPending(): void {
    this.folderService.getPendingFolders({size: 200, sort: 'updatedAt', order: 'desc'}).subscribe({
      next: page => this.pendingFolders = page.content,
      error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
    });
  }

  canEdit(folder: SimpleFolder): boolean {
    return folder.access === 'OWNER' || folder.access === 'EDIT';
  }

  open(folder: SimpleFolder): void {
    void this.router.navigate(['/sammelmappen', folder.id]);
  }

  create(): void {
    this.folderActions.create().subscribe(folder => this.router.navigate(['/sammelmappen', folder.id]));
  }

  rename(folder: SimpleFolder): void {
    this.folderActions.rename(folder).subscribe(() => this.reload());
  }

  duplicate(folder: SimpleFolder): void {
    this.folderActions.duplicate(folder).subscribe(() => this.reload());
  }

  share(folder: SimpleFolder): void {
    this.folderActions.share(folder).subscribe(() => this.reload());
  }

  export(folder: SimpleFolder): void {
    this.folderActions.export(folder);
  }

  delete(folder: SimpleFolder): void {
    this.folderActions.delete(folder).subscribe(() => this.reload());
  }

  leave(folder: SimpleFolder, pending = false): void {
    this.folderActions.leave(folder, pending).subscribe(() => {
      this.reload();
      this.loadPending();
    });
  }

  accept(folder: SimpleFolder): void {
    this.folderActions.accept(folder).subscribe(() => {
      this.reload();
      this.loadPending();
    });
  }
}
